'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Check, X, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface ServicePrice {
  id: string;
  service_key: string;
  label: string;
  base_price: number;
  description: string;
  duration: string;
  is_popular: boolean;
  is_active: boolean;
}

interface AddonService {
  id: string;
  addon_key: string;
  label: string;
  price: number;
  is_active: boolean;
  sort_order: number;
}

const FALLBACK_SERVICES: ServicePrice[] = [
  { id: 'svc-1', service_key: 'standard', label: 'Standard Clean', base_price: 80, description: 'Regular home cleaning service', duration: '2–3 hrs', is_popular: false, is_active: true },
  { id: 'svc-2', service_key: 'deep', label: 'Deep Clean', base_price: 150, description: 'Thorough deep cleaning of entire property', duration: '4–6 hrs', is_popular: true, is_active: true },
  { id: 'svc-3', service_key: 'end-of-tenancy', label: 'End of Tenancy', base_price: 200, description: 'Full end-of-tenancy clean for moving out', duration: '5–8 hrs', is_popular: false, is_active: true },
  { id: 'svc-4', service_key: 'airbnb', label: 'Airbnb Turnover', base_price: 100, description: 'Quick turnaround clean between guests', duration: '2–4 hrs', is_popular: false, is_active: true },
];

const FALLBACK_ADDONS: AddonService[] = [
  { id: 'addon-1', addon_key: 'oven', label: 'Oven Clean', price: 30, is_active: true, sort_order: 1 },
  { id: 'addon-2', addon_key: 'fridge', label: 'Fridge Clean', price: 20, is_active: true, sort_order: 2 },
  { id: 'addon-3', addon_key: 'windows', label: 'Interior Windows', price: 25, is_active: true, sort_order: 3 },
  { id: 'addon-4', addon_key: 'carpet', label: 'Carpet Steam Clean', price: 50, is_active: true, sort_order: 4 },
];

const supabase = createClient();

export default function AdminPricingSection() {
  const [services, setServices] = useState<ServicePrice[]>([]);
  const [addons, setAddons] = useState<AddonService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);

  // Editing state for services
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [editServiceData, setEditServiceData] = useState<Partial<ServicePrice>>({});

  // Editing state for addons
  const [editingAddonId, setEditingAddonId] = useState<string | null>(null);
  const [editAddonData, setEditAddonData] = useState<Partial<AddonService>>({});

  // New addon form
  const [showNewAddon, setShowNewAddon] = useState(false);
  const [newAddon, setNewAddon] = useState({ label: '', price: 0, addon_key: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [svcResult, addonResult] = await Promise.all([
        supabase.from('service_prices').select('*').order('created_at'),
        supabase.from('addon_services').select('*').order('sort_order'),
      ]);
      if (svcResult.error) throw svcResult.error;
      if (addonResult.error) throw addonResult.error;
      setServices(svcResult.data || []);
      setAddons(addonResult.data || []);
      setUsingFallback(false);
    } catch {
      setServices(FALLBACK_SERVICES);
      setAddons(FALLBACK_ADDONS);
      setUsingFallback(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Service price editing ---
  const startEditService = (svc: ServicePrice) => {
    setEditingServiceId(svc.id);
    setEditServiceData({ base_price: svc.base_price, label: svc.label, description: svc.description, duration: svc.duration, is_popular: svc.is_popular });
  };

  const cancelEditService = () => {
    setEditingServiceId(null);
    setEditServiceData({});
  };

  const saveService = async (id: string) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('service_prices')
        .update({ ...editServiceData, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      setEditingServiceId(null);
      setEditServiceData({});
      await fetchData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  // --- Addon editing ---
  const startEditAddon = (addon: AddonService) => {
    setEditingAddonId(addon.id);
    setEditAddonData({ label: addon.label, price: addon.price, is_active: addon.is_active });
  };

  const cancelEditAddon = () => {
    setEditingAddonId(null);
    setEditAddonData({});
  };

  const saveAddon = async (id: string) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('addon_services')
        .update({ ...editAddonData, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      setEditingAddonId(null);
      setEditAddonData({});
      await fetchData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save add-on');
    } finally {
      setSaving(false);
    }
  };

  const deleteAddon = async (id: string) => {
    if (!confirm('Delete this add-on service?')) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('addon_services').delete().eq('id', id);
      if (error) throw error;
      await fetchData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete add-on');
    } finally {
      setSaving(false);
    }
  };

  const createAddon = async () => {
    if (!newAddon.label.trim() || newAddon.price < 0) return;
    setSaving(true);
    try {
      const key = newAddon.label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const { error } = await supabase.from('addon_services').insert({
        addon_key: key + '-' + Date.now(),
        label: newAddon.label.trim(),
        price: newAddon.price,
        is_active: true,
        sort_order: addons.length + 1,
      });
      if (error) throw error;
      setShowNewAddon(false);
      setNewAddon({ label: '', price: 0, addon_key: '' });
      await fetchData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create add-on');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-primary" />
        <span className="ml-2 text-sm text-muted-foreground">Loading pricing data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-bold text-foreground mb-1">Pricing Management</h2>
        <p className="text-sm text-muted-foreground">Set base prices for services and manage add-on options. Changes reflect immediately in the booking flow.</p>
      </div>

      {usingFallback && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-700">
          Showing default pricing data. Connect to the database to manage live prices.
        </div>
      )}

      {error && !usingFallback && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Main Services */}
      <div>
        <h3 className="text-sm font-bold text-foreground mb-3 uppercase tracking-wide">Main Services</h3>
        <div className="space-y-3">
          {services.map((svc) => (
            <div key={svc.id} className="bg-muted/30 rounded-xl border border-border p-4">
              {editingServiceId === svc.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Service Name</label>
                      <input
                        value={editServiceData.label ?? svc.label}
                        onChange={e => setEditServiceData(d => ({ ...d, label: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Base Price (£)</label>
                      <input
                        type="number"
                        min={0}
                        value={editServiceData.base_price ?? svc.base_price}
                        onChange={e => setEditServiceData(d => ({ ...d, base_price: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1">Duration</label>
                      <input
                        value={editServiceData.duration ?? svc.duration}
                        onChange={e => setEditServiceData(d => ({ ...d, duration: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                        placeholder="e.g. 2–3 hrs"
                      />
                    </div>
                    <div className="flex items-end gap-3">
                      <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editServiceData.is_popular ?? svc.is_popular}
                          onChange={e => setEditServiceData(d => ({ ...d, is_popular: e.target.checked }))}
                          className="w-4 h-4 rounded accent-primary"
                        />
                        Mark as Popular
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">Description</label>
                    <input
                      value={editServiceData.description ?? svc.description}
                      onChange={e => setEditServiceData(d => ({ ...d, description: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => saveService(svc.id)}
                      disabled={saving}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all disabled:opacity-60"
                    >
                      {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      Save
                    </button>
                    <button
                      onClick={cancelEditService}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                    >
                      <X size={14} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-bold text-foreground">{svc.label}</p>
                      {svc.is_popular && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary text-white">Popular</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{svc.description} · {svc.duration}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-lg font-extrabold text-primary font-tabular">£{svc.base_price}</span>
                    <button
                      onClick={() => startEditService(svc)}
                      className="p-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Edit price"
                    >
                      <Pencil size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Add-on Services */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-foreground uppercase tracking-wide">Add-on Services</h3>
          <button
            onClick={() => setShowNewAddon(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg gradient-primary text-white text-xs font-semibold hover:opacity-90 active:scale-95 transition-all"
          >
            <Plus size={13} /> Add New
          </button>
        </div>

        {showNewAddon && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 mb-3 space-y-3">
            <p className="text-sm font-semibold text-foreground">New Add-on Service</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Label</label>
                <input
                  value={newAddon.label}
                  onChange={e => setNewAddon(d => ({ ...d, label: e.target.value }))}
                  placeholder="e.g. Carpet Steam Clean"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">Price (£)</label>
                <input
                  type="number"
                  min={0}
                  value={newAddon.price}
                  onChange={e => setNewAddon(d => ({ ...d, price: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={createAddon}
                disabled={saving || !newAddon.label.trim()}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg gradient-primary text-white text-sm font-semibold hover:opacity-90 active:scale-95 transition-all disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Create
              </button>
              <button
                onClick={() => { setShowNewAddon(false); setNewAddon({ label: '', price: 0, addon_key: '' }); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
              >
                <X size={14} /> Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {addons.map((addon) => (
            <div key={addon.id} className="bg-muted/30 rounded-xl border border-border p-3">
              {editingAddonId === addon.id ? (
                <div className="flex items-center gap-3">
                  <input
                    value={editAddonData.label ?? addon.label}
                    onChange={e => setEditAddonData(d => ({ ...d, label: e.target.value }))}
                    className="flex-1 px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-muted-foreground">£</span>
                    <input
                      type="number"
                      min={0}
                      value={editAddonData.price ?? addon.price}
                      onChange={e => setEditAddonData(d => ({ ...d, price: parseInt(e.target.value) || 0 }))}
                      className="w-20 px-3 py-1.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-foreground cursor-pointer whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={editAddonData.is_active ?? addon.is_active}
                      onChange={e => setEditAddonData(d => ({ ...d, is_active: e.target.checked }))}
                      className="w-3.5 h-3.5 rounded accent-primary"
                    />
                    Active
                  </label>
                  <button
                    onClick={() => saveAddon(addon.id)}
                    disabled={saving}
                    className="p-1.5 rounded-lg bg-primary text-white hover:opacity-90 transition-all"
                  >
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                  </button>
                  <button
                    onClick={cancelEditAddon}
                    className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{addon.label}</p>
                    {!addon.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Inactive</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-primary font-tabular">+£{addon.price}</span>
                    <button
                      onClick={() => startEditAddon(addon)}
                      className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      title="Edit"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => deleteAddon(addon.id)}
                      className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {addons.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No add-on services yet. Click &quot;Add New&quot; to create one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
